import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../../../../../components/common/Button";
import { ReactComponent as NotifyIcon } from "../../../../../assets/icons/notify-icon.svg"
import { ReactComponent as InfoIcon } from "../../../../../assets/icons/info.svg";
import {
  getCNYQuotationCategory,
  KlashaCNYQuotation,
} from "../../../redux/slice";
import {
  BalanceExceptionWrapper,
    ContentWrapper,
  Error,
  GenericSubTitle,
  GenericTitle,
  InputWrapper,
  ItemWrapper,
  NotificationWrapper,
  RWrapper,
} from "../styles";
import CustomSelect from "../../../../../components/common/CustomSelect";
import Loader from "../../../../../components/common/Loader";
import InputAndSelect from "../../../../../components/common/InputAndSelect";
import { RateBox } from "../../../components/Withdrawal/styles";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  allowedPurposeMap,
  CHINA_PAYOUT_SOURCE_WALLET_KEY,
  cnyPaymentPurpose,
  getChinaPayoutSourceCurrencies,
  getFilteredPaymentMethods,
  MIN_CNY_AMOUNT,
  paymentMethodConfigurations,
  receiverTypeMap,
  senderTypeMap,
  type ChinaPayoutSourceCurrency,
} from "../../../const";
import { debounce } from "lodash";
import { savedCNYData } from "../../../redux/formDataSlice";
import { encryptPayloadWithMerchantKeyJIT } from "../../../../../utils/encryptionAlgorithm";
import { merchantBusinessKeys } from "../../../../Settings/settingsSlice";
import { formatCurrencyAmount } from "../../../../../utils/currency";
import { capitalizeCurrencyCode } from "../../../../../utils/helpers";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import CustomRadio from "../../../../../components/common/RadioInput";
import { CNYProps, FormInputs } from "../../../../../types/walletTypes";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { FEATURE_FLAG_KEYS } from "../../../../../utils/featureFlags";

type WalletProps = {
  currency: string;
};

type SourceCurrency = ChinaPayoutSourceCurrency;

const getDefaultSourceCurrency = (
  walletsList: WalletProps[] = [],
  enableNgnSource: boolean,
): SourceCurrency => {
  const allowedCurrencies = getChinaPayoutSourceCurrencies(enableNgnSource);
  const available = new Set(
    walletsList
      .map((wallet) => wallet.currency)
      .filter((currency): currency is SourceCurrency =>
        allowedCurrencies.includes(currency as SourceCurrency),
      ),
  );
  const lastUsed = localStorage.getItem(CHINA_PAYOUT_SOURCE_WALLET_KEY);

  if (lastUsed && allowedCurrencies.includes(lastUsed as SourceCurrency)) {
    const storedCurrency = lastUsed as SourceCurrency;
    if (available.has(storedCurrency)) {
      return storedCurrency;
    }
  }
  if (available.has("USD")) return "USD";
  if (enableNgnSource && available.has("NGN")) return "NGN";
  return "USD";
};

const Amount = ({ handleNext }: CNYProps) => {
  const getSavedData = useAppSelector((state) => state.formData.cnyData);
  const location = useLocation();
  const enableNgnChinaPayout = useFeatureFlagEnabled(
    FEATURE_FLAG_KEYS.enableNgnChinaPayout,
  );
  const allowedSourceCurrencies = getChinaPayoutSourceCurrencies(
    !!enableNgnChinaPayout,
  );
  const locationSourceCurrency = (
    location.state as { sourceCurrency?: SourceCurrency } | null
  )?.sourceCurrency;
  const resolvedLocationSourceCurrency =
    locationSourceCurrency &&
    allowedSourceCurrencies.includes(locationSourceCurrency)
      ? locationSourceCurrency
      : undefined;

  const {
    watch,
    control,
    handleSubmit,
    resetField,
    formState: { errors },
  } = useForm<FormInputs>({
    defaultValues: getSavedData || {},
    mode: "onChange"
  });

  const CNYData = useAppSelector((state) => state.formData?.cnyData || {});
  const [userData, setUserData] = useState<FormInputs>(() => ({
    ...CNYData,
    ...(resolvedLocationSourceCurrency
      ? { sourceCurrency: resolvedLocationSourceCurrency }
      : {}),
  }));

  const { keys } = useAppSelector((state) => state.settings);
  const { wallets, loading } = useAppSelector((state) => state?.wallets || {});
  const { businessId } = useAppSelector((state) => state.users);
  const { currencyCode } = useParams();
  const dispatch = useAppDispatch();

  const filteredWallets = wallets as WalletProps[];
  const sourceCurrency = userData?.sourceCurrency || "USD";

  const getAllowedPurposes = (paymentMethod: string) => {
    const config = paymentMethodConfigurations[paymentMethod];
    if (!config) return [];

    const key = `${config.transferType}_${config.service}`;
    const allowedNames = allowedPurposeMap[key];
    if (!allowedNames) return cnyPaymentPurpose;

    return cnyPaymentPurpose.filter((option) =>
      allowedNames.includes(option.value),
    );
  };

  const IDENTITY_TYPE = watch("senderIdentity");
  const RECEIVER_TYPE = watch("accountType");
  const SENDER_TYPE = watch("receiverAccountType");

  const senderTypeOptions = senderTypeMap[IDENTITY_TYPE];

  const receiverTypeOptions = receiverTypeMap[RECEIVER_TYPE];

  const CURRENT_WALLET = wallets?.find(
    (wallet) => wallet.currency === sourceCurrency,
  );

  const uniqueCurrencies = Array.from(
    new Set(filteredWallets?.map((wallet) => wallet.currency)),
  );


  const options = uniqueCurrencies.map((currency) => ({
    label: currency,
    value: currency,
  }));

  useEffect(() => {
    if (!wallets?.length) return;

    setUserData((prev) => {
      if (
        prev?.sourceCurrency &&
        allowedSourceCurrencies.includes(
          prev.sourceCurrency as ChinaPayoutSourceCurrency,
        )
      ) {
        return prev;
      }
      if (resolvedLocationSourceCurrency) {
        return {
          ...prev,
          sourceCurrency: resolvedLocationSourceCurrency,
        };
      }
      return {
        ...prev,
        sourceCurrency: getDefaultSourceCurrency(
          wallets,
          !!enableNgnChinaPayout,
        ),
      };
    });
  }, [
    wallets,
    resolvedLocationSourceCurrency,
    enableNgnChinaPayout,
    allowedSourceCurrencies,
  ]);

  useEffect(() => {
    if (!CURRENT_WALLET) return;

    const availableBalance = parseFloat(
      Number(CURRENT_WALLET.availableBalance).toFixed(2),
    );
    const totalAmount = parseFloat((userData?.totalAmount ?? 0).toFixed(2));

    if (availableBalance < totalAmount) {
      handleDataUpdate("balanceError", true);
    } else {
      handleDataUpdate("balanceError", false);
    }
  }, [CURRENT_WALLET, userData?.totalAmount]);

  const walletToDebit = options?.filter((option) =>
    allowedSourceCurrencies.includes(option.value as ChinaPayoutSourceCurrency),
  );

  const handleAmountChange = (enteredAmount) => {
    setUserData((prevData) => ({
      ...prevData,
      amount: enteredAmount,
    }));
  };

  const handleDataUpdate = (field: keyof FormInputs, value: any) => {
    setUserData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearQuoteFields = (prev: FormInputs): FormInputs => ({
    ...prev,
    rate: null,
    fee: null,
    totalAmount: 0,
    quotationId: undefined,
    reference: undefined,
    sourceAmount: undefined,
    convertedAmount: undefined,
    expiration: undefined,
    rateError: false,
  });

  const handleSourceCurrencyChange = (currency: string) => {
    localStorage.setItem(CHINA_PAYOUT_SOURCE_WALLET_KEY, currency);
    setUserData((prev) => ({
      ...clearQuoteFields(prev),
      sourceCurrency: currency as FormInputs["sourceCurrency"],
    }));
  };

  useEffect(() => {
    dispatch(savedCNYData(userData));
  }, [userData, dispatch]);

  useEffect(() => {
    if (businessId) {
      dispatch(merchantBusinessKeys(businessId));
    }
  }, [businessId, dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      const action = await dispatch(getCNYQuotationCategory());
      if (getCNYQuotationCategory.fulfilled.match(action)) {
        handleDataUpdate("category", action.payload);
      }
    };

    fetchData();
  }, [dispatch]);


  const requestIdRef = useRef(0);

  const debouncedGetKlashaQuotation = useCallback(
      debounce(async (userData, reqId: number) => {
        if (
            !userData?.amount ||
            Number(userData?.amount) < MIN_CNY_AMOUNT ||
            !userData?.paymentMethod ||
            !userData?.purpose ||
            !userData?.sourceCurrency ||
            !businessId
        ) {
          return;
        }

        const config = paymentMethodConfigurations[userData.paymentMethod] || {
          serviceCode: "",
          service: "",
          fundSource: "",
        };

        const data = {
          sourceCurrency: userData.sourceCurrency,
          destinationCurrency: "CNY",
          destinationAmount: userData.amount,
          ...config,
        };

        try {
          const { encryptedMessage, publicKey } = await encryptPayloadWithMerchantKeyJIT(
            businessId,
            data,
          );
          const payload = { message: encryptedMessage };
          const token = publicKey || keys?.data?.publicKey;

          const action = await dispatch(
              KlashaCNYQuotation({ message: payload, token }),
          );

          if (reqId !== requestIdRef.current) return;

          if (KlashaCNYQuotation.fulfilled.match(action)) {
            const { fxRate, fee, sourceAmount, expiration, id, reference } =
                action.payload;

            const convertedValue = Number(userData?.amount / fxRate);

            setUserData((prev) => ({
              ...prev,
              rate: fxRate,
              convertedAmount: convertedValue,
              totalAmount: Number(sourceAmount + fee),
              fee,
              expiration,
              quotationId: id,
              reference,
              sourceAmount,
              rateError: false,
            }));
          } else {
            setUserData((prev) => ({
              ...prev,
              rate: null,
              fee: null,
              totalAmount: 0,
              quotationId: undefined,
              reference: undefined,
              sourceAmount: undefined,
              rateError: true,
            }));
          }
        } catch (err) {
          if (reqId !== requestIdRef.current) return;
          setUserData((prev) => ({
            ...prev,
            rate: null,
            fee: null,
            totalAmount: 0,
            quotationId: undefined,
            reference: undefined,
            sourceAmount: undefined,
            rateError: true,
          }));
        }
      }, 1000),
      [dispatch, businessId, keys?.data?.publicKey]
  );

  const { amount, paymentMethod, purpose } = userData;

  useEffect(() => {
    const numAmount = Number(amount);
    if (
      amount &&
      numAmount >= MIN_CNY_AMOUNT &&
      paymentMethod &&
      purpose &&
      sourceCurrency &&
      businessId
    ) {
      const nextReqId = requestIdRef.current + 1;
      requestIdRef.current = nextReqId;
      debouncedGetKlashaQuotation({ ...userData, sourceCurrency }, nextReqId);
    } else {
      debouncedGetKlashaQuotation.cancel();
      requestIdRef.current++;
      setUserData((prev) => {
        if (
          prev.rate === null &&
          prev.fee === null &&
          prev.totalAmount === 0 &&
          prev.quotationId === undefined &&
          prev.reference === undefined &&
          prev.sourceAmount === undefined &&
          prev.convertedAmount === undefined &&
          prev.expiration === undefined &&
          !prev.rateError
        ) {
          return prev;
        }
        return clearQuoteFields(prev);
      });
    }
  }, [
    amount,
    paymentMethod,
    purpose,
    sourceCurrency,
    businessId,
    debouncedGetKlashaQuotation,
  ]);

  useEffect(() => {
    return () => {
      debouncedGetKlashaQuotation.cancel();
    };
  }, [debouncedGetKlashaQuotation]);


  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    const updatedData: FormInputs = {
      ...data,
      senderIdentity: data.senderIdentity,
      senderIdType: data.senderIdType,
      receiverIdType: data.receiverIdType,
      quotationId: userData?.quotationId,
      paymentMethod: userData.paymentMethod,
      purpose: userData.purpose,
      sourceCurrency: userData.sourceCurrency,
    };

    if (userData.sourceCurrency) {
      localStorage.setItem(
        CHINA_PAYOUT_SOURCE_WALLET_KEY,
        userData.sourceCurrency,
      );
    }

    dispatch(savedCNYData(updatedData));
    handleNext?.();
  };

  const rateFeeCurrency = sourceCurrency === "NGN" ? "NGN" : "";

  return (
    <ContentWrapper>
      <Loader isLoading={loading} />

      <InputWrapper>
        <GenericTitle>
          Are you the ultimate sender for this transaction?
        </GenericTitle>
        <GenericSubTitle>
          Accurate identification is crucial for compliance with financial
          regulations.
        </GenericSubTitle>
        <Controller
          name="senderIdentity"
          control={control}
          rules={{ required: "This field is required" }}
          render={({ field }) => (
            <ItemWrapper>
              {["Yes", "No"].map((option) => (
                <RWrapper key={option}>
                  <CustomRadio
                    label={option}
                    value={option}
                    onClick={() => {
                      field.onChange(option);
                      resetField("receiverAccountType");
                      resetField("accountType");
                      resetField("purpose");

                      handleDataUpdate("paymentMethod", null);
                      handleDataUpdate("purpose", null);
                    }}
                    selected={field.value === option}
                  />
                </RWrapper>
              ))}
            </ItemWrapper>
          )}
        />
        {<Error>{errors?.senderIdentity?.message}</Error>}
      </InputWrapper>

      {IDENTITY_TYPE && (
        <InputWrapper>
          <Controller
            key={IDENTITY_TYPE}
            name="accountType"
            control={control}
            rules={{ required: "Sender type is required" }}
            render={({ field }) => (
              <CustomSelect
                value={field.value}
                options={senderTypeOptions}
                onChange={(val) => {
                  field.onChange(val);
                  resetField("receiverAccountType");
                  handleDataUpdate("paymentMethod", null);
                  handleDataUpdate("purpose", null);

                }}
                $width="100%"
                placeholder="Sender type"
                lightBorder
              />
            )}
          />
          {<Error>{errors?.senderIdType?.message}</Error>}
        </InputWrapper>
      )}

      <InputWrapper>
        <Controller
          name="receiverAccountType"
          control={control}
          rules={{ required: "Receiver type is required" }}
          render={({ field }) => (
            <CustomSelect
              value={field.value}
              options={receiverTypeOptions}
              onChange={field.onChange}
              $width="100%"
              placeholder="Receiver type"
              lightBorder
            />
          )}
        />
        {<Error>{errors?.receiverIdType?.message}</Error>}
      </InputWrapper>

      <CustomSelect
        $width="100%"
        placeholder="Payment method"
        options={getFilteredPaymentMethods(SENDER_TYPE, RECEIVER_TYPE, {
          includeWeChat: !!enableNgnChinaPayout,
        }).map((option) => ({
          label: option.name,
          value: option.value,
        }))}
        onChange={(data) => {
          handleDataUpdate("paymentMethod", data);
          const allowed = getAllowedPurposes(data);
          if (userData?.purpose && !allowed.some((option) => option.value === userData?.purpose)) {
            handleDataUpdate("purpose", null);
          }
        }}
        value={userData?.paymentMethod}
        lightBorder
      />

      <CustomSelect
        $width="100%"
        placeholder="Purpose"
        options={getAllowedPurposes(paymentMethod).map((option) => ({
          label: option.name,
          value: option.value,
        }))}
        onChange={(data) => handleDataUpdate("purpose", data)}
        value={userData?.purpose}
        lightBorder
      />

      <InputAndSelect
        amountValue={userData?.amount}
        width="100%"
        placeholder="Amount"
        options={[{ label: "CNY", value: "CNY" }]}
        selectValue={capitalizeCurrencyCode(currencyCode)}
        onAmountChange={handleAmountChange}
      />
      {Boolean(userData?.amount && Number(userData?.amount) < MIN_CNY_AMOUNT) && (
        <Error>Minimum payout amount is {MIN_CNY_AMOUNT} CNY</Error>
      )}

      {userData?.rate != null && (
        <section>
          <RateBox>
            <span className="fee-box">
              x
              <span>
                {formatCurrencyAmount(rateFeeCurrency, userData?.rate ?? 0, 2) ?? 0}
              </span>
            </span>
            <span>Exchange rate</span>
          </RateBox>
        </section>
      )}

      {userData?.fee != null && (
        <section>
          <RateBox>
            <span className="fee-box">
              +
              <span>
                {formatCurrencyAmount(rateFeeCurrency, userData?.fee ?? 0, 2) ?? 0}
              </span>
            </span>
            <span>Fees</span>
          </RateBox>
        </section>
      )}

      {userData?.rateError && (
        <Error>
          Exchange rate unavailable. Please try again or contact support.
        </Error>
      )}

      {userData?.totalAmount > 0 && (
        <div>
          <InputAndSelect
            amountValue={userData?.totalAmount?.toFixed(2) ?? ""}
            width="100%"
            placeholder="Total you will be charged"
            options={walletToDebit}
            selectValue={sourceCurrency}
            onSelectChange={
              enableNgnChinaPayout ? handleSourceCurrencyChange : undefined
            }
            disableSelect={!enableNgnChinaPayout}
          />
          {enableNgnChinaPayout && sourceCurrency === "NGN" && (
            <NotificationWrapper>
              <InfoIcon />
              <p>This will be deducted from your NGN wallet.</p>
            </NotificationWrapper>
          )}
          {userData?.balanceError && (
            <BalanceExceptionWrapper>
              <NotifyIcon /> <span>Exceeds wallet balance.</span>
              <Link to={`/wallets/${sourceCurrency.toLowerCase()}`}> Fund wallet</Link>
            </BalanceExceptionWrapper>
          )}
        </div>
      )}

      <Button
        label="Continue"
        onClick={handleSubmit(onSubmit)}
        height="48px"
        disabled={
          loading ||
          !userData?.amount ||
          Number(userData?.amount) < MIN_CNY_AMOUNT ||
          !userData?.rate ||
          !userData?.paymentMethod ||
          !userData?.purpose ||
          userData?.balanceError ||
          userData?.rateError
        }
      />
    </ContentWrapper>
  );
};

export default Amount;
